import { useState } from 'react';

/**
 * יצירת קשר.
 *
 * THE PAGE IS ALWAYS SERVED. The address is the only thing that varies: with one, the form composes
 * a message and sends it; without one, the same form is shown disabled and says why. A form that
 * accepts a message and drops it is the worst of the three — it fails after someone has already
 * written to you, and they have no way to know.
 *
 * SENT THROUGH THE VISITOR'S OWN MAIL CLIENT, not through an endpoint we do not have. A `mailto:`
 * with the fields composed into it costs no backend, no spam handling and nowhere to keep messages;
 * it works while the API is down; and the sender keeps a copy in their own Sent folder, which no
 * form post gives them. The trade is that it opens their mail app, so the page says so rather than
 * letting it surprise them.
 *
 * NOT `login@huaca.io`. That address is the magic-link SENDER (`MAIL_FROM` in the API's config) — a
 * send-only identity on a verified domain. Publishing it would route a customer's question into a
 * mailbox that may not be read, or may not receive at all.
 */

/** The address to publish. `null` while there is none — see above.
 *  Cast because a `const` initialised to `null` narrows to `null`, and every guard below would then
 *  be checking a type that cannot hold an address. */
export const CONTACT_EMAIL = null as string | null;

/**
 * Always true — the page is served either way.
 *
 * Kept as a function because the page table asks every page the same question, and because this is
 * the one line that changes if the page ever needs gating again: not the routing, not the nav.
 */
export const contactReady = (): boolean => true;

/**
 * What someone is writing about.
 *
 * Written for THIS product rather than lifted from a generic contact form — "a source we do not
 * cover yet" is the single most useful thing a visitor can tell us, and it only appears on a list
 * somebody thought about.
 */
const SUBJECTS = ['שאלה לפני הרשמה', 'מקור שלא מכוסה עדיין', 'משהו לא עובד', 'חשבונית ותשלום', 'אחר'] as const;

export interface ContactForm {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const EMPTY: ContactForm = { name: '', email: '', phone: '', subject: '', message: '' };

/**
 * The whole form as a `mailto:`.
 *
 * Exported so the composition is testable on its own — the alternative is asserting a URL by
 * clicking a button that navigates the window away, which jsdom cannot do and a browser cannot be
 * asked to do twice.
 *
 * The message is capped in the textarea rather than truncated here: several mail clients silently
 * drop a `mailto:` past roughly 2,000 characters, and a message cut in half on arrival is worse than
 * one that would not fit in the box.
 */
export function composeMailto(to: string, form: ContactForm): string {
  const lines = [
    `שם: ${form.name}`,
    `אימייל: ${form.email}`,
    ...(form.phone.trim() ? [`טלפון: ${form.phone}`] : []),
    '',
    form.message,
  ];
  const subject = form.subject || 'פנייה מהאתר';
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`;
}

/**
 * Takes the address as a PROP rather than reading the constant, so both states are renderable by the
 * suite. The one that ships today is the one with no address — the state most likely to go untested
 * precisely because it is temporary.
 */
export function ContactPage({ email }: { email: string | null }) {
  const [form, setForm] = useState<ContactForm>(EMPTY);
  const set =
    (field: keyof ContactForm) =>
    (event: { target: { value: string } }): void =>
      setForm((current) => ({ ...current, [field]: event.target.value }));

  return (
    <section className="tenders-band tenders-band-page">
      <div className="tenders-band-wrap">
        <p className="tenders-landing-eyebrow">יצירת קשר</p>
        <h1>מדברים איתנו ישירות.</h1>
        <p className="tenders-landing-lede">
          שלחו הודעה וצוות קטן יקרא אותה. אין מוקד ואין מספר פנייה — התשובה מגיעה מאותם אנשים שבונים
          את הלוח.
        </p>

        {/*
          `.tenders-lightpin` because the controls are @huaca/ui's: `input`, `select`, `textarea` and
          `label` are styled in components.css against tokens that FOLLOW THE THEME. On this page's
          fixed cream ground that is the masthead bug over again — a dark field on pale paper for any
          visitor whose OS is dark. The pin quotes light mode for the whole subtree, exactly as the
          worked example and the sign-in card already do.
        */}
        <form
          className="tenders-form tenders-lightpin"
          onSubmit={(event) => {
            event.preventDefault();
            if (email === null) return;
            // `required` and the address format have already been enforced by the browser here.
            window.location.href = composeMailto(email, form);
          }}
        >
          {/*
            One attribute disables every control inside it, the button included, and it is what a
            screen reader announces — better than styling each field to look inert and hoping.
          */}
          <fieldset disabled={email === null}>
            <div className="tenders-form__row">
              <div className="tenders-form__field">
                <label htmlFor="tenders-contact-name">שם מלא</label>
                <input
                  id="tenders-contact-name"
                  name="name"
                  autoComplete="name"
                  required
                  value={form.name}
                  onChange={set('name')}
                />
              </div>
              <div className="tenders-form__field">
                <label htmlFor="tenders-contact-email">אימייל</label>
                {/* `type="email"` so the browser validates it and a phone offers the right keyboard.
                    `dir="ltr"` on the field alone — an address is Latin inside a Hebrew page. */}
                <input
                  id="tenders-contact-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  dir="ltr"
                  required
                  value={form.email}
                  onChange={set('email')}
                />
              </div>
            </div>

            <div className="tenders-form__row">
              <div className="tenders-form__field">
                <label htmlFor="tenders-contact-phone">טלפון (לא חובה)</label>
                <input
                  id="tenders-contact-phone"
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  dir="ltr"
                  value={form.phone}
                  onChange={set('phone')}
                />
              </div>
              <div className="tenders-form__field">
                <label htmlFor="tenders-contact-subject">נושא הפנייה</label>
                <select
                  id="tenders-contact-subject"
                  name="subject"
                  value={form.subject}
                  onChange={set('subject')}
                >
                  <option value="">בחרו נושא</option>
                  {SUBJECTS.map((subject) => (
                    <option key={subject} value={subject}>
                      {subject}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="tenders-form__field">
              <label htmlFor="tenders-contact-message">הודעה</label>
              <textarea
                id="tenders-contact-message"
                name="message"
                rows={6}
                required
                /* Capped so the composed `mailto:` stays inside what mail clients carry intact. */
                maxLength={1500}
                value={form.message}
                onChange={set('message')}
              />
            </div>

            <button className="tenders-landing-cta tenders-form__send" type="submit">
              שליחת הודעה
            </button>
          </fieldset>

          {email === null ? (
            <p className="tenders-form__note">
              עוד אין כתובת שאפשר לפרסם, ולכן הטופס סגור. הוא ייפתח ברגע שתהיה — עדיף שתראו אותו כבוי
              מאשר שיבלע הודעה.
            </p>
          ) : (
            <p className="tenders-form__note">
              השליחה נפתחת בתוכנת המייל שלכם עם ההודעה מוכנה, כך שנשאר לכם עותק בדואר היוצא.
            </p>
          )}
        </form>
      </div>
    </section>
  );
}
